import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import type { Performers as PerformersType } from '@/types';
import { PerformersList } from './components/PerformersList';
import { getPreviewPerformers, sanitizePerformers } from './utils';
import styles from './Performers.module.scss';

const PERFORMERS_PREVIEW_THRESHOLD = 15;

export type PerformersProps = NonNullable<PerformersType>;

export const Performers: React.FC<PerformersProps> = ({ headliners = [], supporters = [] }) => {
  const { formatMessage } = useIntl();

  const sanitizedHeadliners = sanitizePerformers(headliners);
  const sanitizedSupporters = sanitizePerformers(supporters);

  const totalPerformers = sanitizedHeadliners.length + sanitizedSupporters.length;
  const hasPreview = totalPerformers > PERFORMERS_PREVIEW_THRESHOLD;
  const [isExpanded, setIsExpanded] = useState(!hasPreview);

  if (totalPerformers === 0) {
    return null;
  }

  const shouldShowPreview = hasPreview && !isExpanded;

  const { previewHeadliners, previewSupporters } = getPreviewPerformers(
    sanitizedHeadliners,
    sanitizedSupporters,
    PERFORMERS_PREVIEW_THRESHOLD,
  );

  const shouldShowHeadlinersTitle = sanitizedSupporters.length > 0;
  const shouldShowSupportersTitle = sanitizedHeadliners.length > 0;

  return (
    <div aria-labelledby="performers-heading" data-testid="performers-section">
      <h2 id="performers-heading" className={styles.title}>
        {formatMessage({
          id: 'Performers',
          defaultMessage: 'Performers',
          description: 'Performers section title',
        })}
      </h2>

      {shouldShowPreview && (
        <div aria-hidden="true" data-testid="performers-preview">
          <PerformersList
            headliners={previewHeadliners}
            supporters={previewSupporters}
            shouldShowHeadlinersTitle={shouldShowHeadlinersTitle}
            shouldShowSupportersTitle={shouldShowSupportersTitle}
          />
        </div>
      )}

      <PerformersList
        headliners={sanitizedHeadliners}
        supporters={sanitizedSupporters}
        shouldShowHeadlinersTitle={shouldShowHeadlinersTitle}
        shouldShowSupportersTitle={shouldShowSupportersTitle}
        isHidden={shouldShowPreview}
        data-testid="performers-full-list"
      />

      {hasPreview && (
        <button onClick={() => setIsExpanded((previous) => !previous)} className={styles.toggleButton}>
          {formatMessage(
            isExpanded
              ? {
                  id: 'Show less',
                  defaultMessage: 'Show less',
                  description: 'Collapses performers list back to preview',
                }
              : {
                  id: 'Show all performers',
                  defaultMessage: 'Show all performers',
                  description: 'Expands performers list to show all items',
                },
          )}
        </button>
      )}
    </div>
  );
};
